import React from "react";
import { motion } from "framer-motion";
import {
  UsersRound,
  UserPlus,
  UserRound,
  HeartPulse,
  CalendarCheck,
  FileText,
  ShieldCheck,
  Check,
  ArrowRight,
  Baby,
  UserRoundCheck,
  Stethoscope,
  ClipboardList,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { Link } from "react-router-dom";

const FamilyCare = () => {
  const familyServices = [
    {
      icon: UserPlus,
      title: "Add Family Members",
      description:
        "Organise healthcare for supported family members from your connected Doxzon experience.",
    },
    {
      icon: CalendarCheck,
      title: "Manage Appointments",
      description:
        "Find and manage supported healthcare appointments for your family members.",
    },
    {
      icon: FileText,
      title: "Health Records",
      description:
        "Access supported health information and records for family members in one place.",
    },
    {
      icon: HeartPulse,
      title: "Continue Care",
      description:
        "Keep your family's healthcare journey connected across supported Doxzon services.",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: UserPlus,
      title: "Add a Family Member",
      description:
        "Set up a supported family member within your family care experience.",
    },
    {
      number: "02",
      icon: UserRoundCheck,
      title: "Choose Their Care",
      description:
        "Find the appropriate healthcare service based on their needs.",
    },
    {
      number: "03",
      icon: CalendarCheck,
      title: "Book & Manage",
      description:
        "Schedule and manage supported appointments and healthcare services.",
    },
    {
      number: "04",
      icon: ClipboardList,
      title: "Stay Connected",
      description:
        "Keep relevant healthcare information organised across their care journey.",
    },
  ];

  const familyTypes = [
    {
      icon: Baby,
      title: "Children",
      description:
        "Help manage supported healthcare needs and appointments for children.",
    },
    {
      icon: UserRound,
      title: "Parents",
      description:
        "Stay connected with supported healthcare services for parents and older family members.",
    },
    {
      icon: UsersRound,
      title: "Dependents",
      description:
        "Organise supported healthcare journeys for family members who depend on you.",
    },
    {
      icon: HeartPulse,
      title: "Family Wellness",
      description:
        "Keep everyday healthcare and wellness needs easier to organise.",
    },
  ];

  const benefits = [
    "Keep family healthcare organised",
    "Manage supported family members from one experience",
    "Find healthcare services for your family",
    "Schedule supported appointments",
    "Access relevant health information",
    "Keep care journeys connected",
  ];

  const faqs = [
    {
      question: "What is Family Care?",
      answer:
        "Family Care is designed to help patients organise and manage supported healthcare services for family members through a connected Doxzon experience.",
    },
    {
      question: "Can I add family members?",
      answer:
        "Where the family-care functionality is supported, you can add eligible family members and manage their supported healthcare needs.",
    },
    {
      question: "Can I book appointments for family members?",
      answer:
        "Where supported, family members' healthcare appointments can be scheduled and managed through the family-care workflow.",
    },
    {
      question: "Can I access my family's health records?",
      answer:
        "Access to another person's health information depends on the supported family relationship, permissions and applicable privacy requirements.",
    },
    {
      question: "Can I manage healthcare for children?",
      answer:
        "Where supported, Family Care can help parents or authorised caregivers organise healthcare services for children.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Family Care
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Care for your
                <span className="block text-teal-500 mt-2">
                  whole family.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Make it easier to organise healthcare for the people who
                matter most with a connected family-care experience.
              </p>

              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-xl bg-teal-50 grid place-items-center">

                    <UsersRound
                      size={22}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="flex-1">

                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                      Family Care
                    </p>

                    <p className="font-semibold mt-1">
                      Manage healthcare together
                    </p>

                  </div>

               <Link to="/joinmembership">
  <motion.div
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-5 h-12 text-sm font-semibold"
  >
    Get Started
    <ArrowRight size={15} />
  </motion.div>
</Link>

                </div>

                <button className="sm:hidden w-full mt-4 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm">
                  Get Started
                </button>

              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Privacy-conscious care
                </span>

                <span className="inline-flex items-center gap-2">
                  <UsersRound size={16} className="text-teal-500" />
                  Family-focused
                </span>

              </div>

            </motion.div>

            {/* RIGHT FAMILY CARD */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      My Family
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Family Members
                    </h3>

                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                    <UsersRound
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* FAMILY MEMBERS */}

                <div className="space-y-3 mt-6">

                  {[
                    ["You", "Primary account", UserRound],
                    ["Family Member", "Healthcare profile", HeartPulse],
                    ["Child", "Dependent profile", Baby],
                  ].map(([name, role, Icon], index) => (

                    <motion.div
                      key={name}
                      whileHover={{ x: 4 }}
                      className={`flex items-center gap-4 rounded-2xl p-4 border ${
                        index === 0
                          ? "border-teal-100 bg-teal-50/60"
                          : "border-slate-100 bg-slate-50"
                      }`}
                    >

                      <div className="w-11 h-11 rounded-xl bg-white grid place-items-center">

                        <Icon
                          size={19}
                          className="text-teal-600"
                        />

                      </div>

                      <div className="flex-1">

                        <p className="text-sm font-semibold">
                          {name}
                        </p>

                        <p className="text-[11px] text-slate-400 mt-1">
                          {role}
                        </p>

                      </div>

                      <ArrowRight
                        size={16}
                        className="text-slate-300"
                      />

                    </motion.div>

                  ))}

                </div>

                {/* FAMILY STATUS */}

                <div className="mt-5 rounded-2xl bg-slate-950 text-white p-5">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-xl bg-teal-500/10 grid place-items-center">

                      <ShieldCheck
                        size={17}
                        className="text-teal-400"
                      />

                    </div>

                    <div>

                      <p className="text-xs font-semibold">
                        Connected Family Care
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        Manage supported healthcare services
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            FAMILY SERVICES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Family Services
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                One place for
                <span className="text-teal-500">
                  {" "}family care.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Organise supported healthcare needs for your family through
                connected Doxzon services.
              </p>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {familyServices.map((item, index) => {

    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.5,
          delay: index * 0.06,
        }}
        whileHover={{ y: -4 }}
        className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
          <Icon
            size={17}
            className="text-teal-600 group-hover:text-white transition"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

      </motion.div>
    );

  })}

</div>

          </div>

        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Family care made
                <span className="text-teal-500">
                  {" "}simpler.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {steps.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAMILY TYPES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Who It Helps
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Care for every
                <span className="text-teal-500">
                  {" "}stage of life.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Support your family's healthcare journey with services
                designed around different care needs.
              </p>

            </div>

       <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {familyTypes.map((item, index) => {

    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.06 }}
        whileHover={{ y: -4 }}
        className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center">
          <Icon
            size={17}
            className="text-teal-600"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

      </motion.div>
    );

  })}

</div>

          </div>

        </section>

        {/* =====================================================
            CONNECTED CARE
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Connected Family Care
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Keep every care journey
                <span className="block text-teal-400">
                  connected.
                </span>

              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-xl">
                From finding care to appointments and health information,
                connect supported family healthcare workflows through Doxzon.
              </p>

            </div>

            <div className="space-y-3">

              {[
                [Stethoscope, "Find Care"],
                [CalendarCheck, "Appointments"],
                [HeartPulse, "Healthcare Services"],
                [FileText, "Health Records"],
              ].map(([Icon, title], index) => (

                <motion.div
                  key={title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >

                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Icon
                      size={18}
                      className="text-teal-400"
                    />

                  </div>

                  <span className="text-sm font-medium text-slate-300">
                    {title}
                  </span>

                  <ArrowRight
                    size={16}
                    className="ml-auto text-slate-500"
                  />

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Family Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Less coordination.
                <span className="block text-teal-500">
                  More connected care.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6 max-w-xl">
                Doxzon helps make supported family healthcare services easier
                to organise and manage.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Family Care
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Make healthcare
                <span className="text-teal-400">
                  {" "}easier for your family.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Organise supported family healthcare services through one
                connected Doxzon experience.
              </p>

           <Link
  to="/joinmembership"
  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
>
  Get Started
  <ArrowRight size={17} />
</Link>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default FamilyCare;